import { supabase } from './supabaseClient';

interface MemoryInfo {
    usedMB: number | null;
    totalMB: number | null;
    limitMB: number | null;
}

const getMemoryInfo = (): MemoryInfo | null => {
    try {
        // Chrome에서만 사용 가능한 performance.memory
        if (!('memory' in performance)) {
            return { usedMB: null, totalMB: null, limitMB: null };
        }

        const memory = performance?.memory;
        if (!memory || typeof memory !== 'object')
            return { usedMB: null, totalMB: null, limitMB: null };

        const convertToMB = (value: unknown): number | null => {
            if (typeof value !== 'number' || isNaN(value) || value < 0) {
                return null;
            }
            return Number((value / 1024 / 1024).toFixed(2));
        };

        return {
            usedMB:
                'usedJSHeapSize' in memory
                    ? convertToMB(memory.usedJSHeapSize)
                    : null,
            totalMB:
                'totalJSHeapSize' in memory
                    ? convertToMB(memory.totalJSHeapSize)
                    : null,
            limitMB:
                'jsHeapSizeLimit' in memory
                    ? convertToMB(memory.jsHeapSizeLimit)
                    : null,
        };
    } catch (e) {
        console.warn('메모리 정보 조회 실패:', e);
        return null;
    }
};

export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';

/**
 * reportError로 넘어오는 값의 실제 런타임 형태.
 * TanStack Query onError 등에서 Error 인스턴스가 아닌 Supabase PostgrestError
 * 본문({ message, code, details, hint })이 그대로 throw되는 경우가 있어,
 * name/stack이 없는 객체까지 포괄한다.
 */
interface ErrorLike {
    name?: string;
    message?: string;
    stack?: string;
    code?: string | number;
    details?: string;
    hint?: string;
}

/**
 * error_type 결정 규칙.
 * 1) Error 인스턴스는 name 사용
 * 2) name이 없고 code가 있으면(PostgrestError 등) `postgrest:{code}`
 *    (예: 42501=RLS 위반, 23505=unique 위반, PGRST116=단건 조회 실패)
 * 3) 둘 다 없으면 UnknownError
 */
const resolveErrorType = (error: ErrorLike): string => {
    if (error?.name) return error.name;
    const code = error?.code;
    if (code !== undefined && code !== null && `${code}` !== '') {
        return `postgrest:${code}`;
    }
    return 'UnknownError';
};

export const errorReportingService = {
    async reportError(
        userId: string | null,
        workspaceId: string | null,
        error: Error,
        featureModule: string,
        additionalContext: Record<string, any> = {},
        severity: ErrorSeverity = 'error'
    ) {
        // 개발 환경에서는 로그만
        if (
            process.env.NODE_ENV === 'development' ||
            window.location.hostname === 'localhost'
        ) {
            console.warn('Would report error:', {
                error,
                featureModule,
                additionalContext,
                severity,
            });
            return;
        }

        try {
            const memoryInfo = getMemoryInfo();
            const err = error as ErrorLike;

            // console.log({
            //     user_id: userId,
            //     workspace_id: workspaceId,
            //     error_type: error.name,
            //     error_message: error.message,
            //     error_stack: error.stack,

            //     user_agent: navigator.userAgent,
            //     page_url: window.location.href,

            //     memory_used_mb: memoryInfo?.usedMB || null,
            //     memory_total_mb: memoryInfo?.totalMB || null,
            //     memory_limit_mb: memoryInfo?.limitMB || null,

            //     feature_module: featureModule,

            //     error_context: {
            //         ...additionalContext,
            //         timestamp: new Date().toISOString(),
            //         browser: navigator.userAgent.includes('Chrome')
            //             ? 'chrome'
            //             : 'other',
            //     },
            // });

            const { error: errorLogError } = await supabase
                .from('error_logs')
                .insert({
                    user_id: userId,
                    workspace_id: workspaceId,
                    error_type: resolveErrorType(err),
                    error_message: err.message,
                    error_stack: err.stack,

                    user_agent: navigator.userAgent,
                    page_url: window.location.href,

                    memory_used_mb: memoryInfo?.usedMB ?? null,
                    memory_total_mb: memoryInfo?.totalMB ?? null,
                    memory_limit_mb: memoryInfo?.limitMB ?? null,

                    feature_module: featureModule,
                    severity: severity,

                    error_context: {
                        ...additionalContext,
                        // PostgrestError의 진단 필드는 error_type/message만으로
                        // 드러나지 않아(어느 정책·컬럼인지 등) 있을 때만 보존한다.
                        ...(err.code != null ? { errorCode: err.code } : {}),
                        ...(err.details ? { errorDetails: err.details } : {}),
                        ...(err.hint ? { errorHint: err.hint } : {}),
                        timestamp: new Date().toISOString(),
                        browser: navigator.userAgent.includes('Chrome')
                            ? 'chrome'
                            : 'other',
                    },
                });

            if (errorLogError) {
                console.error('에러 리포팅 저장 실패:', errorLogError);
            }
        } catch (e) {
            console.error('에러 리포팅 실패:', e);
        }
    },
};
